/* ==========================================================================
   Gift frame customizer
   --------------------------------------------------------------------------
   Personalise-or-as-shown choice, photo upload, signature text, live overlay
   preview, automatic slot placement, full-size preview modal, and a Save step
   that flattens everything to one PNG and attaches it to the cart.

   Notes:
   - Inputs live inside {% form 'product' %}, so Shopify submits them as line
     item properties. Trade's product-form.js sends a FormData body with the
     Content-Type header deleted, which is what makes file uploads work over
     the AJAX cart API.
   - Positions are percentages of the frame image, so the on-screen preview and
     the exported PNG use identical maths and cannot drift apart.
   - The export loads its own copy of the artwork with crossOrigin set. The
     visible <img> deliberately does not set it: if the CDN ever stopped
     sending CORS headers, only saving would fail rather than the whole page
     losing its image.
   ========================================================================== */

(function () {
  'use strict';

  var ACCEPTED = ['image/png', 'image/jpeg', 'image/webp'];
  var MAX_EXPORT = 1600;

  function init(root) {
    if (root.dataset.giftReady === 'true') return;
    root.dataset.giftReady = 'true';

    var photoInput = root.querySelector('[data-gift-photo-input]');
    var nameInput = root.querySelector('[data-gift-name-input]');
    var designInput = root.querySelector('[data-gift-design-input]');
    var preview = root.querySelector('[data-gift-preview]');
    var photoPreview = root.querySelector('[data-gift-photo-preview]');
    var namePreview = root.querySelector('[data-gift-name-preview]');
    var filename = root.querySelector('[data-gift-filename]');
    var errorBox = root.querySelector('[data-gift-error]');
    var savedBox = root.querySelector('[data-gift-saved]');
    var maxSize = parseInt(root.dataset.maxSize, 10) || 20971520;

    if (!photoInput || !nameInput || !preview) return;

    var baseImage = preview.querySelector('.gift-preview__base');
    var designSaved = false;

    function num(key, fallback) {
      var value = parseFloat(preview.dataset[key]);
      return isNaN(value) ? fallback : value;
    }

    /* ---------------------------------------------------------------- error */

    function showError(message) {
      if (!errorBox) return;
      errorBox.textContent = message;
      errorBox.hidden = false;
    }

    function clearError() {
      if (!errorBox) return;
      errorBox.textContent = '';
      errorBox.hidden = true;
    }

    /* Any edit invalidates a previously saved design. */
    function invalidate() {
      designSaved = false;
      if (designInput) designInput.value = '';
      if (savedBox) savedBox.hidden = true;
    }

    /* ---------------------------------------------------------------- photo */

    function clearPhoto() {
      photoInput.value = '';
      photoPreview.removeAttribute('src');
      preview.dataset.hasPhoto = 'false';
      if (filename) {
        filename.textContent = '';
        filename.hidden = true;
      }
      invalidate();
    }

    function renderFilename(name) {
      if (!filename) return;
      filename.textContent = '';
      filename.hidden = false;
      filename.appendChild(document.createTextNode(name + ' '));

      var remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'gift-customizer__remove';
      remove.textContent = 'Remove';
      remove.addEventListener('click', function () {
        clearPhoto();
        clearError();
      });
      filename.appendChild(remove);
    }

    photoInput.addEventListener('change', function () {
      var file = this.files && this.files[0];
      invalidate();

      if (!file) {
        clearPhoto();
        return;
      }

      if (ACCEPTED.indexOf(file.type) === -1) {
        showError('Please choose a JPG, PNG or WebP image.');
        clearPhoto();
        return;
      }

      if (file.size > maxSize) {
        showError('That photo is too large. Please choose an image under 20 MB.');
        clearPhoto();
        return;
      }

      clearError();

      var reader = new FileReader();
      reader.onload = function (event) {
        photoPreview.src = event.target.result;
        preview.dataset.hasPhoto = 'true';
      };
      reader.onerror = function () {
        showError('We could not read that file. Please try another photo.');
        clearPhoto();
      };
      reader.readAsDataURL(file);

      renderFilename(file.name);
    });

    /* ------------------------------------------------------------ signature */

    function renderName() {
      var value = nameInput.value.trim();
      namePreview.textContent = value ? '— ' + value : '';
    }

    nameInput.addEventListener('input', function () {
      renderName();
      invalidate();
      if (nameInput.value.trim()) clearError();
    });

    /* ------------------------------------------------- scaling and border */

    function scaleSignature(stage) {
      var nameEl = stage.querySelector('[data-gift-name-preview]');
      if (!nameEl) return;
      var pct = parseFloat(nameEl.dataset.size) || 4;
      var width = stage.clientWidth;
      if (!width) return;
      nameEl.style.fontSize = (width * pct) / 100 + 'px';
    }

    /* CSS cannot express a border as a percentage of width, so set it in px. */
    function applyBorder(stage) {
      var el = stage.querySelector('[data-gift-border]');
      if (!el) return;

      var style = stage.dataset.borderStyle || 'none';
      if (style === 'none') {
        el.style.borderWidth = '0';
        return;
      }

      var pct = parseFloat(stage.dataset.borderWidth) || 2;
      el.style.borderStyle = style === 'double' ? 'double' : 'solid';
      el.style.borderWidth = Math.max(3, (stage.clientWidth * pct) / 100) + 'px';
    }

    function relayout(stage) {
      scaleSignature(stage);
      applyBorder(stage);
    }

    function relayoutLive() {
      relayout(preview);
    }

    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(relayoutLive).observe(preview);
    } else {
      window.addEventListener('resize', relayoutLive);
    }

    if (baseImage && !baseImage.complete) {
      baseImage.addEventListener('load', relayoutLive);
    }

    relayoutLive();
    renderName();

    /* -------------------------------------------------- automatic placement
       Every artwork has its letter text in a different spot, so shared default
       coordinates land the photo and signature on top of it. gift-autoplace.js
       reads the artwork's pixels and finds the clear areas.

       Only applied where the merchant has not set the metafield: an explicit
       value on the product always wins. */

    function applyPlacement(place) {
      if (!place) return;

      if (place.photo && preview.dataset.autoPhoto === 'true') {
        preview.dataset.photoX = place.photo.x.toFixed(2);
        preview.dataset.photoY = place.photo.y.toFixed(2);
        preview.dataset.photoW = place.photo.w.toFixed(2);
        preview.dataset.photoH = place.photo.h.toFixed(2);

        [preview.querySelector('[data-gift-slot]'), photoPreview].forEach(function (el) {
          if (!el) return;
          el.style.left = place.photo.x + '%';
          el.style.top = place.photo.y + '%';
          el.style.width = place.photo.w + '%';
          el.style.height = place.photo.h + '%';
        });
      }

      if (place.name && preview.dataset.autoName === 'true') {
        preview.dataset.nameX = place.name.x.toFixed(2);
        preview.dataset.nameY = place.name.y.toFixed(2);
        namePreview.dataset.size = place.name.size.toFixed(2);
        namePreview.style.left = place.name.x + '%';
        namePreview.style.top = place.name.y + '%';
      }

      relayoutLive();
    }

    if (
      window.GiftAutoPlace &&
      (preview.dataset.autoPhoto === 'true' || preview.dataset.autoName === 'true')
    ) {
      window.GiftAutoPlace.fromUrl(
        (baseImage && (baseImage.currentSrc || baseImage.src)) || ''
      ).then(applyPlacement);
    }

    /* --------------------------------------------------------- compositing */

    function roundedPath(ctx, x, y, w, h, r) {
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + w - r, y);
      ctx.quadraticCurveTo(x + w, y, x + w, y + r);
      ctx.lineTo(x + w, y + h - r);
      ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
      ctx.lineTo(x + r, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - r);
      ctx.lineTo(x, y + r);
      ctx.quadraticCurveTo(x, y, x + r, y);
      ctx.closePath();
    }

    function clipSlot(ctx, x, y, w, h, W) {
      var shape = preview.dataset.shape;

      if (shape === 'circle') {
        ctx.beginPath();
        ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
      } else if (shape === 'rounded') {
        roundedPath(ctx, x, y, w, h, Math.min(W * 0.015, w / 2, h / 2));
      } else {
        ctx.beginPath();
        ctx.rect(x, y, w, h);
      }

      ctx.clip();
    }

    function drawPhoto(ctx, W, H) {
      return new Promise(function (resolve, reject) {
        var src = photoPreview.getAttribute('src');
        if (!src) {
          resolve();
          return;
        }

        var img = new Image();

        img.onload = function () {
          var dx = (W * num('photoX', 35)) / 100;
          var dy = (H * num('photoY', 8)) / 100;
          var dw = (W * num('photoW', 30)) / 100;
          var dh = (H * num('photoH', 22)) / 100;

          ctx.save();
          clipSlot(ctx, dx, dy, dw, dh, W);

          // Reproduce CSS object-fit: cover.
          var scale = Math.max(dw / img.naturalWidth, dh / img.naturalHeight);
          var sw = dw / scale;
          var sh = dh / scale;
          var sx = (img.naturalWidth - sw) / 2;
          var sy = (img.naturalHeight - sh) / 2;

          ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
          ctx.restore();
          resolve();
        };

        img.onerror = function () {
          reject(new Error('We could not read your photo. Please try another one.'));
        };

        img.src = src;
      });
    }

    function drawName(ctx, W, H) {
      var text = namePreview.textContent;
      if (!text) return;

      var computed = window.getComputedStyle(namePreview);
      var px = (W * (parseFloat(namePreview.dataset.size) || 4)) / 100;

      ctx.font = px + 'px ' + (computed.fontFamily || 'sans-serif');
      ctx.fillStyle = computed.color || '#2b2b2b';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, (W * num('nameX', 50)) / 100, (H * num('nameY', 88)) / 100);
    }

    function drawBorder(ctx, W, H) {
      var style = preview.dataset.borderStyle || 'none';
      if (style === 'none') return;

      var total = (W * (parseFloat(preview.dataset.borderWidth) || 2)) / 100;
      ctx.strokeStyle = preview.dataset.borderColor || '#2b2b2b';

      if (style === 'double') {
        // Matches CSS `double`: line, gap, line, each a third of the width.
        var t = total / 3;
        ctx.lineWidth = t;
        ctx.strokeRect(t / 2, t / 2, W - t, H - t);
        ctx.strokeRect(total - t / 2, total - t / 2, W - 2 * total + t, H - 2 * total + t);
      } else {
        ctx.lineWidth = total;
        ctx.strokeRect(total / 2, total / 2, W - total, H - total);
      }
    }

    function compose() {
      return new Promise(function (resolve, reject) {
        var art = new Image();
        art.crossOrigin = 'anonymous';

        art.onload = function () {
          var natW = art.naturalWidth || 1200;
          var natH = art.naturalHeight || 1200;
          var W = Math.min(natW, MAX_EXPORT);
          var H = Math.round(natH * (W / natW));

          var canvas = document.createElement('canvas');
          canvas.width = W;
          canvas.height = H;

          var ctx = canvas.getContext('2d');
          ctx.drawImage(art, 0, 0, W, H);

          drawPhoto(ctx, W, H)
            .then(function () {
              drawName(ctx, W, H);
              drawBorder(ctx, W, H);

              canvas.toBlob(function (blob) {
                if (blob) resolve(blob);
                else reject(new Error('Could not render the image.'));
              }, 'image/png');
            })
            .catch(reject);
        };

        art.onerror = function () {
          reject(new Error('Could not load the frame artwork for saving.'));
        };

        art.src = (baseImage && (baseImage.currentSrc || baseImage.src)) || '';
      });
    }

    /* ---------------------------------------------------------------- mode
       "As shown" customers get the plain frame. The personalisation fields are
       disabled, not just hidden, because disabled controls are left out of the
       FormData Shopify receives — otherwise the order would carry empty Photo
       and From properties. The radio itself is a property, so the order tells
       you which one the customer chose. */

    var personaliseWrap = root.querySelector('[data-gift-personalise]');
    var modeInputs = root.querySelectorAll('[data-gift-mode]');

    function currentMode() {
      var checked = root.querySelector('[data-gift-mode]:checked');
      return checked ? checked.dataset.giftMode : 'custom';
    }

    function applyMode() {
      var plain = currentMode() === 'plain';

      if (personaliseWrap) personaliseWrap.hidden = plain;

      photoInput.disabled = plain;
      nameInput.disabled = plain;
      if (designInput) designInput.disabled = plain;

      preview.dataset.plain = plain ? 'true' : 'false';

      if (plain) {
        clearError();
        // Display only — the underlying values survive a switch back.
        preview.dataset.hasPhoto = 'false';
        namePreview.textContent = '';
      } else {
        if (photoPreview.getAttribute('src')) preview.dataset.hasPhoto = 'true';
        renderName();
      }
    }

    Array.prototype.forEach.call(modeInputs, function (input) {
      input.addEventListener('change', applyMode);
    });

    applyMode();

    /* --------------------------------------------------------------- modal */

    var modal = root.querySelector('[data-gift-modal]');
    var modalBody = root.querySelector('[data-gift-modal-body]');
    var modalNote = root.querySelector('[data-gift-modal-note]');
    var openButton = root.querySelector('[data-gift-open-preview]');
    var saveButton = root.querySelector('[data-gift-save]');
    var lastFocused = null;
    var defaultNote = modalNote ? modalNote.textContent : '';

    function note(message) {
      if (modalNote) modalNote.textContent = message;
    }

    function openModal() {
      if (!modal || !modalBody) return;

      modalBody.textContent = '';
      var clone = preview.cloneNode(true);
      clone.removeAttribute('data-gift-preview');
      modalBody.appendChild(clone);

      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      lastFocused = document.activeElement;
      note(defaultNote);

      requestAnimationFrame(function () {
        relayout(clone);
      });

      var closeButton = modal.querySelector('.gift-modal__close');
      if (closeButton) closeButton.focus();
    }

    function closeModal() {
      if (!modal || modal.hidden) return;

      modal.hidden = true;
      modalBody.textContent = '';
      document.body.style.overflow = '';

      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    function resetSaveButton() {
      if (!saveButton) return;
      saveButton.disabled = false;
      saveButton.textContent = 'Save this design';
    }

    function save() {
      if (!photoInput.files || !photoInput.files.length) {
        note('Add a photo before saving.');
        return;
      }
      if (!nameInput.value.trim()) {
        note('Add the name to sign with before saving.');
        return;
      }
      if (!designInput) return;

      saveButton.disabled = true;
      saveButton.textContent = 'Saving…';

      compose()
        .then(function (blob) {
          var file = new File([blob], 'frame-design.png', { type: 'image/png' });
          var transfer = new DataTransfer();
          transfer.items.add(file);
          designInput.files = transfer.files;

          designSaved = true;
          if (savedBox) savedBox.hidden = false;
          clearError();
          resetSaveButton();
          closeModal();
        })
        .catch(function (err) {
          note(err.message || 'Saving failed. Please try again.');
          resetSaveButton();
        });
    }

    if (openButton) openButton.addEventListener('click', openModal);
    if (saveButton) saveButton.addEventListener('click', save);

    if (modal) {
      modal.querySelectorAll('[data-gift-modal-close]').forEach(function (el) {
        el.addEventListener('click', closeModal);
      });

      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') closeModal();
      });

      window.addEventListener('resize', function () {
        if (modal.hidden) return;
        var stage = modalBody.querySelector('.gift-preview');
        if (stage) relayout(stage);
      });
    }

    /* ------------------------------------------------------------ validation
       Capture phase, so this runs before Trade's <product-form> submit handler
       and stopImmediatePropagation can cancel the add-to-cart request. */

    var form = root.closest('form');
    if (!form) return;

    form.addEventListener(
      'submit',
      function (event) {
        // Nothing to check when the customer wants the frame as shown.
        if (currentMode() === 'plain') {
          clearError();
          return;
        }

        var missingPhoto = !photoInput.files || !photoInput.files.length;
        var missingName = !nameInput.value.trim();

        if (!missingPhoto && !missingName && designSaved) {
          clearError();
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        if (missingPhoto && missingName) {
          showError('Please add a photo and your name before adding to cart.');
          photoInput.focus();
        } else if (missingPhoto) {
          showError('Please choose a photo before adding to cart.');
          photoInput.focus();
        } else if (missingName) {
          showError('Please enter the name to sign the letter with.');
          nameInput.focus();
        } else {
          showError('Please open the preview and save your design before adding to cart.');
          if (openButton) openButton.focus();
        }

        root.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
      true
    );
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-gift-customizer]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
